import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'

import {
  fetchDeclarations,
  issueDeclaration,
  oeffneProjektdatei,
  previewDeclaration,
  renderDeclaration,
  withdrawDeclaration,
  type DeclarationState,
} from '../api/project-tech.api'
import { alsZeitpunkt } from '../project-labels'
import { TechFieldsCard } from './TechFieldsCard'

/** `inlineInput` ohne `w-full` — sonst überstimmt es jede gesetzte Breite. */
const schmal = inlineInput.replace('w-full', '')

/**
 * AP-35 Detailblatt 6 2.6 und 3.5 — **Reiter Technik an der Position.**
 *
 * Links das Technik-Blatt, rechts die Leistungserklärung: Status, Prüfliste als
 * Ampel, Ausstellen, Versionen mit PDF und Etikett. Nach jedem Speichern der
 * Technik wird die Karte neu gelesen — „Stand weicht ab" muss sofort stimmen.
 */
export function PositionTechTab({ projectId, positionId }: { projectId: string; positionId: string }) {
  const { user } = useAuthStore()
  const darfTechnik = erfuelltRecht('projects.position.manage', user?.permissions)
  const [stand, setStand] = useState(0)
  // Ausstellen schreibt die geerbten Werte an der Position fest (Nachtrag 4) — das Blatt neu laden.
  const [ausgestellt, setAusgestellt] = useState(0)

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <TechFieldsCard
        key={ausgestellt}
        projectId={projectId}
        positionId={positionId}
        darfAendern={darfTechnik}
        onSaved={() => setStand((s) => s + 1)}
      />
      <DeclarationCard projectId={projectId} positionId={positionId} stand={stand} onIssued={() => setAusgestellt((a) => a + 1)} />
    </div>
  )
}

const AMPEL = {
  ok: { Icon: CheckCircle2, klasse: 'text-green-600 dark:text-green-400' },
  block: { Icon: XCircle, klasse: 'text-destructive' },
  warn: { Icon: AlertTriangle, klasse: 'text-amber-600 dark:text-amber-400' },
  info: { Icon: Info, klasse: 'text-muted-foreground' },
} as const

function DeclarationCard({
  projectId,
  positionId,
  stand,
  onIssued,
}: {
  projectId: string
  positionId: string
  stand: number
  onIssued: () => void
}) {
  const { user } = useAuthStore()
  const darfAusstellen = erfuelltRecht('projects.declaration.issue', user?.permissions)
  const [daten, setDaten] = useState<DeclarationState | null>(null)
  const [sprache, setSprache] = useState<'DE' | 'EN'>('DE')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [rueckzug, setRueckzug] = useState<{ id: string; grund: string } | null>(null)

  const laden = useCallback(async () => {
    try {
      setDaten(await fetchDeclarations(projectId, positionId))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Leistungserklärung konnte nicht geladen werden.'))
    }
  }, [projectId, positionId])

  useEffect(() => {
    void laden()
  }, [laden, stand])

  async function tu(aktion: () => Promise<DeclarationState | void>) {
    setLaeuft(true)
    setFehler(null)
    try {
      const neu = await aktion()
      if (neu) setDaten(neu)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  if (!daten) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null
  const aktuell = daten.versions.find((v) => v.status === 'ISSUED') ?? null
  const gesperrt = daten.check.some((z) => z.level === 'block' && !z.ok)

  return (
    <div className={`${formCardSm} space-y-3`}>
      <h3 className={cardTitle}>Leistungserklärung</h3>

      <p className="text-sm">
        {aktuell ? (
          <>
            <span className="font-medium">{aktuell.number}</span> · Version {aktuell.version} ausgestellt am{' '}
            {alsZeitpunkt(aktuell.issuedAt)}
            {daten.deviates && (
              <span className="mt-1 block text-amber-700 dark:text-amber-400">
                Stand weicht von Version {aktuell.version} ab — neue Version ausstellen.
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">Noch keine gültige Erklärung.</span>
        )}
      </p>

      <ul className="space-y-1 text-sm">
        {daten.check.map((z) => {
          const art = z.ok && z.level !== 'info' ? AMPEL.ok : AMPEL[z.level]
          return (
            <li key={z.key} className="flex gap-2">
              <art.Icon size={16} className={`mt-0.5 shrink-0 ${art.klasse}`} />
              <span>
                {z.text}
                {!!z.details?.length && <span className="block text-xs text-muted-foreground">{z.details.join(', ')}</span>}
              </span>
            </li>
          )
        })}
      </ul>

      {darfAusstellen && (
        <div className="flex flex-wrap items-center gap-2">
          <select className={`${schmal} w-20`} value={sprache} onChange={(e) => setSprache(e.target.value as 'DE' | 'EN')} aria-label="Sprache">
            <option value="DE">DE</option>
            <option value="EN">EN</option>
          </select>
          <button
            type="button"
            disabled={laeuft}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
            onClick={() => void tu(() => previewDeclaration(projectId, positionId, sprache))}
          >
            Vorschau
          </button>
          <button
            type="button"
            disabled={laeuft || gesperrt}
            title={gesperrt ? 'Die Prüfliste hat rote Punkte.' : undefined}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            onClick={() =>
              void tu(async () => {
                const neu = await issueDeclaration(projectId, positionId, sprache)
                onIssued()
                return neu
              })
            }
          >
            {aktuell ? 'Neue Version ausstellen' : 'Ausstellen'}
          </button>
          <span className="basis-full text-[11px] text-muted-foreground">
            Beim Ausstellen werden die vom Projekt übernommenen Technikwerte an dieser Position festgeschrieben — spätere
            Änderungen am Projekt wirken hier nicht mehr.
          </span>
        </div>
      )}

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}

      {daten.versions.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground">
            <tr>
              <th className="px-2 py-1 text-left">Nummer</th>
              <th className="px-2 py-1 text-left">Stand</th>
              <th className="px-2 py-1 text-left">Dateien</th>
              <th className="px-2 py-1" />
            </tr>
          </thead>
          <tbody>
            {daten.versions.map((v) => (
              <tr key={v.id} className="border-t border-border align-top">
                <td className="px-2 py-1">
                  <span className="font-medium">{v.number}</span>
                  <span className="block text-xs text-muted-foreground">
                    V{v.version} · {v.language} · {alsZeitpunkt(v.issuedAt)}
                    {v.issuedByName && ` · ${v.issuedByName}`}
                  </span>
                </td>
                <td className="px-2 py-1">
                  {v.status === 'ISSUED' ? (
                    <span className="text-green-700 dark:text-green-400">gültig</span>
                  ) : (
                    <span className="text-muted-foreground">
                      zurückgezogen
                      {v.withdrawReason && <span className="block text-xs">{v.withdrawReason}</span>}
                    </span>
                  )}
                </td>
                <td className="px-2 py-1">
                  {v.pdfFileId ? (
                    <span className="flex gap-2">
                      <button type="button" className="text-primary hover:underline" onClick={() => void tu(() => oeffneProjektdatei(v.pdfFileId!, `${v.number}.pdf`))}>
                        PDF
                      </button>
                      {v.ceLabelFileId && (
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={() => void tu(() => oeffneProjektdatei(v.ceLabelFileId!, `${v.number}-CE.pdf`))}
                        >
                          CE-Etikett
                        </button>
                      )}
                    </span>
                  ) : darfAusstellen ? (
                    <button type="button" className="text-xs underline" onClick={() => void tu(() => renderDeclaration(projectId, v.id))}>
                      PDF erzeugen
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-2 py-1 text-right">
                  {darfAusstellen && v.status === 'ISSUED' && (
                    <button type="button" className="text-xs text-destructive hover:underline" onClick={() => setRueckzug({ id: v.id, grund: '' })}>
                      zurückziehen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rueckzug && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <label className="block text-xs text-muted-foreground">
            Grund des Zurückziehens
            <input
              className={inlineInput}
              value={rueckzug.grund}
              autoFocus
              onChange={(e) => setRueckzug({ ...rueckzug, grund: e.target.value })}
            />
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" className="rounded-md border border-border px-3 py-1 text-sm" onClick={() => setRueckzug(null)}>
              Abbrechen
            </button>
            <button
              type="button"
              disabled={!rueckzug.grund.trim() || laeuft}
              className="rounded-md bg-destructive px-3 py-1 text-sm text-destructive-foreground disabled:opacity-50"
              onClick={() =>
                void tu(async () => {
                  const neu = await withdrawDeclaration(projectId, rueckzug.id, rueckzug.grund.trim())
                  setRueckzug(null)
                  return neu
                })
              }
            >
              Zurückziehen
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
